import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MainLayout from "../layout/MainLayout";
import { useAlertContext } from "../context/AlertContext";
import { useAuth } from "../context/AuthContext";
import {
    finalizeAdaptationCheckpoint,
    forceCompleteAdaptationCheckpoint,
    generateAdaptationDocuments,
    getAdaptationCheckpoint,
    resetAdaptationCheckpointAnswers,
    rescheduleAdaptationCheckpoint,
    submitAdaptationAnswer,
} from "../services/adaptationApi";
import { MOCK_ADAPTATION_ITEMS } from "../data/mockAdaptation";
import AdaptationQuestionFields from "../adaptation/AdaptationQuestionFields";
import { formFor, formPublicTitle, normalizeQuestionnaire } from "../adaptation/catalog";
import { formatDateRu, ROLE_EMPLOYEE, ROLE_HR, ROLE_MANAGER, rolesForKind } from "../adaptation/rules";
import { managerSafeReport } from "../adaptation/reports";
import AdaptationTakeLinks from "../adaptation/AdaptationTakeLinks";

const ROLE_TITLES = {
    [ROLE_EMPLOYEE]: "Сотрудник",
    [ROLE_MANAGER]: "Руководитель",
    [ROLE_HR]: "HR",
};

function fallbackCheckpoint(checkpointId) {
    return MOCK_ADAPTATION_ITEMS.find((i) => String(i.id) === String(checkpointId)) || null;
}

export default function AdaptationDetailPage() {
    const { checkpointId } = useParams();
    const navigate = useNavigate();
    const { showAlert } = useAlertContext();
    const { user } = useAuth() || {};
    const isHr = ["superadmin", "admin", "manager", "senior_manager", "hr", "owner", "dev", "tech_admin", "director"].includes(user?.role);
    const [row, setRow] = useState(() => fallbackCheckpoint(checkpointId));
    const [role, setRole] = useState(isHr ? ROLE_HR : ROLE_MANAGER);
    const [values, setValues] = useState({});
    const [saving, setSaving] = useState(false);
    const [resetting, setResetting] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await getAdaptationCheckpoint(checkpointId);
                if (!cancelled && data?.id) setRow(data);
            } catch {
                if (!cancelled) setRow(fallbackCheckpoint(checkpointId));
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [checkpointId]);

    useEffect(() => {
        if (!row) return;
        const existing = (row.answers || []).find((a) => a.role === role);
        setValues(existing?.payload || {});
    }, [row, role]);

    // [Claude] 2026-11-02 — этап из конструктора: свои участники (например, наставник), №7
    useEffect(() => {
        if (!isHr || !row?.builder || !Array.isArray(row.participants) || !row.participants.length) return;
        if (!row.participants.some((p) => p.role === role)) setRole(row.participants[row.participants.length - 1].role);
    }, [row, role, isHr]);

    if (!row) {
        return (
            <MainLayout>
                <p className="text-slate-500">Этап не найден.</p>
            </MainLayout>
        );
    }

    const participating = new Set(
        row.builder && Array.isArray(row.participants) ? row.participants.map((p) => p.role) : rolesForKind(row.kind)
    );
    const roleTitle = (r) => ROLE_TITLES[r] || (row.participants || []).find((p) => p.role === r)?.label || r;
    const questions = row.builder
        ? ((row.form && row.form[role]) || [])
        : normalizeQuestionnaire(
            row.kind,
            role,
            (row.form && row.form[role]) || formFor(row.kind, role)
        );
    const title = (row.form_title && row.form_title[role]) || formPublicTitle(row.kind, role);
    const existing = (row.answers || []).find((a) => a.role === role);
    const locked = Boolean(existing) && role !== ROLE_HR;
    const safe = managerSafeReport(row);

    const onChange = (id, value) => setValues((prev) => ({ ...prev, [id]: value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!participating.has(role)) {
            showAlert("Эта роль не участвует в этапе", "error");
            return;
        }
        if (locked) {
            showAlert("Ответы уже отправлены и заблокированы", "error");
            return;
        }
        setSaving(true);
        try {
            const updated = await submitAdaptationAnswer(row.id, { role, payload: values });
            setRow(updated);
            showAlert("Ответы сохранены", "success");
        } catch (err) {
            showAlert(err.message || "Не удалось сохранить (проверьте API)", "error");
        } finally {
            setSaving(false);
        }
    };

    const refresh = async () => setRow(await getAdaptationCheckpoint(row.id));

    const reschedule = async () => {
        const planDate = window.prompt("Новая дата этапа (ГГГГ-ММ-ДД)", row.plan_date || "");
        if (!planDate) return;
        const reason = window.prompt("Причина переноса");
        if (!reason) return;
        try {
            await rescheduleAdaptationCheckpoint(row.id, { plan_date: planDate, reason });
            await refresh();
            showAlert("Этап перенесён, будущие напоминания перестроены", "success");
        } catch (error) { showAlert(error.message, "error"); }
    };

    const forceComplete = async () => {
        const reason = window.prompt("Причина принудительного завершения");
        if (!reason) return;
        try {
            await forceCompleteAdaptationCheckpoint(row.id, { reason });
            await refresh();
            showAlert("Этап завершён принудительно; поздние ответы останутся доступны", "success");
        } catch (error) { showAlert(error.message, "error"); }
    };

    const resetAnswers = async () => {
        if (!window.confirm("Сбросить все ответы только этого этапа? История изменений сохранится, а формы снова станут доступны для заполнения.")) return;
        const reason = window.prompt("Причина сброса ответов");
        if (!reason?.trim()) return;
        if (reason.trim().length < 3) {
            showAlert("Укажите причину сброса минимум из 3 символов", "error");
            return;
        }
        if (reason.trim().length > 2000) {
            showAlert("Причина сброса не должна превышать 2000 символов", "error");
            return;
        }
        setResetting(true);
        try {
            const updated = await resetAdaptationCheckpointAnswers(row.id, { reason: reason.trim() });
            setRow(updated);
            showAlert("Ответы этапа сброшены. Формы можно заполнить повторно.", "success");
        } catch (error) {
            showAlert(error.message || "Не удалось сбросить ответы этапа", "error");
        } finally {
            setResetting(false);
        }
    };

    const finalize = async () => {
        const comment = window.prompt("Комментарий HR к итогу", "") ?? "";
        try {
            await finalizeAdaptationCheckpoint(row.id, { outcome: row.outcome || "stable", risk: row.risk === "uncalculated" ? "low" : row.risk, comment });
            await refresh();
            showAlert("Итог этапа зафиксирован", "success");
        } catch (error) { showAlert(error.message, "error"); }
    };

    const generateDocuments = async () => {
        try {
            await generateAdaptationDocuments(row.id, { formats: ["docx", "pdf"], final: row.status === "completed" });
            showAlert("Версии документов DOCX и PDF сформированы", "success");
        } catch (error) { showAlert(error.message, "error"); }
    };

    return (
        <MainLayout className="flex h-full flex-col overflow-y-auto bg-slate-50/70">
            <button type="button" className="mb-4 w-fit rounded-xl px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-white hover:text-slate-900" onClick={() => navigate("/tests/adaptation")}>
                ← К списку адаптации
            </button>
            <section className="mb-6 overflow-hidden rounded-[28px] bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 p-6 text-white shadow-xl shadow-slate-200 md:p-8">
                <div className="flex flex-wrap items-start justify-between gap-5"><div>
                <p className="text-xs font-bold tracking-[0.2em] text-amber-300">ЭТАП АДАПТАЦИИ</p>
                <h1 className="mt-2 text-3xl font-bold tracking-tight md:text-4xl" data-testid="adaptation-detail-title">{row.full_name}</h1>
                <p className="mt-3 text-base text-slate-300">{row.position || "Должность не указана"}</p>
                </div><span className="rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm font-semibold backdrop-blur">{row.status_label}</span></div>
                <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    <div className="rounded-2xl bg-white/10 p-4"><span className="text-xs uppercase tracking-wide text-slate-400">Опрос</span><b className="mt-1 block">{title}</b></div>
                    <div className="rounded-2xl bg-white/10 p-4"><span className="text-xs uppercase tracking-wide text-slate-400">Плановая дата</span><b className="mt-1 block">{formatDateRu(row.plan_date)}</b></div>
                    <div className="rounded-2xl bg-white/10 p-4"><span className="text-xs uppercase tracking-wide text-slate-400">Текущий риск</span><b className="mt-1 block">{row.risk_label || "Не рассчитан"}</b></div>
                </div>
                {row.enrollment_id ? (
                    <button
                        type="button"
                        className="mt-5 inline-flex rounded-xl bg-white/10 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/20"
                        onClick={() => navigate(`/tests/adaptation/case/${row.enrollment_id}`)}
                    >
                        Карточка сотрудника
                    </button>
                ) : null}
                {isHr ? (
                    <div className="mt-4 flex flex-wrap gap-2 border-t border-white/10 pt-4">
                        <button type="button" onClick={reschedule} className="rounded-xl border border-white/20 bg-white/10 px-4 py-2.5 text-sm font-semibold hover:bg-white/20">Перенести этап</button>
                        <button type="button" onClick={forceComplete} className="rounded-xl border border-white/20 bg-white/10 px-4 py-2.5 text-sm font-semibold hover:bg-white/20">Завершить принудительно</button>
                        <button type="button" onClick={finalize} className="rounded-xl bg-amber-400 px-4 py-2.5 text-sm font-bold text-slate-950 hover:bg-amber-300">Зафиксировать итог</button>
                        <button type="button" onClick={generateDocuments} className="rounded-xl border border-white/20 bg-white/10 px-4 py-2.5 text-sm font-semibold hover:bg-white/20">Сформировать документы</button>
                        {(row.available_actions || []).includes("reset_answers") ? (
                            <button
                                type="button"
                                onClick={resetAnswers}
                                disabled={resetting}
                                className="rounded-xl border border-rose-300/40 bg-rose-500/10 px-4 py-2.5 text-sm font-semibold text-rose-100 hover:bg-rose-500/20 disabled:opacity-50"
                                data-testid="reset-adaptation-answers"
                            >
                                {resetting ? "Сбрасываем…" : "Сбросить ответы этапа"}
                            </button>
                        ) : null}
                    </div>
                ) : null}
            </section>

            <div className="grid gap-5 lg:grid-cols-3">
                <form onSubmit={onSubmit} className="space-y-6 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm lg:col-span-2 md:p-7">
                    <div><h2 className="text-xl font-bold text-slate-900">Анкета этапа</h2><p className="mt-1 text-sm text-slate-500">Переключайтесь между участниками, чтобы посмотреть заполнение каждой формы.</p></div>
                    <div className="flex flex-wrap gap-2 rounded-2xl bg-slate-100 p-1.5">
                        {(isHr ? (row.builder && Array.isArray(row.participants) ? row.participants.map((p) => p.role) : [ROLE_EMPLOYEE, ROLE_MANAGER, ROLE_HR]) : [ROLE_MANAGER]).map((r) => (
                            <button
                                key={r}
                                type="button"
                                disabled={!participating.has(r)}
                                onClick={() => setRole(r)}
                                className={`min-h-10 flex-1 rounded-xl px-4 py-2 text-sm font-semibold transition ${
                                    role === r ? "bg-slate-900 text-white shadow" : "text-slate-600 hover:bg-white"
                                } disabled:opacity-40`}
                            >
                                {roleTitle(r)}
                            </button>
                        ))}
                    </div>
                    {isHr && (row.form_links || []).length ? (
                        <div className="rounded-xl bg-slate-50 p-3">
                            <b className="mb-2 block text-sm text-slate-800">Ссылки для прохождения</b>
                            <AdaptationTakeLinks links={(row.form_links || []).map((item) => ({ ...item, kind_label: row.kind_label, plan_date: row.plan_date, checkpoint_id: row.id }))} employeeId={row.employee_id} fullName={row.full_name} />
                        </div>
                    ) : null}
                    {locked ? (
                        <p className="text-sm text-amber-700 bg-amber-50 rounded-xl px-3 py-2">
                            Ответы этой роли уже отправлены и заблокированы. HR может править свой комментарий.
                        </p>
                    ) : null}
                    <AdaptationQuestionFields
                        questions={questions}
                        values={values}
                        onChange={onChange}
                        coreHistory={row.core_history || {}}
                        disabled={locked}
                    />
                    <button
                        type="submit"
                        disabled={saving || locked || !participating.has(role) || questions.length === 0}
                        className="min-h-12 rounded-2xl bg-slate-950 px-6 py-3 text-base font-bold text-white shadow-lg transition hover:bg-slate-800 disabled:opacity-50"
                        data-testid="save-answers"
                    >
                        Сохранить ответы
                    </button>
                </form>
                <aside className="space-y-4 lg:sticky lg:top-4 lg:self-start">
                    <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                        <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Итог этапа</p>
                        <p className="mt-2 text-xl font-bold text-slate-900">{row.outcome || "Пока не определён"}</p>
                        <p className="mt-2 rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-600">Риск: <b>{row.risk_label || "Не рассчитан"}</b></p>
                    </div>
                    <div className="rounded-3xl border border-sky-100 bg-sky-50 p-5" data-testid="manager-safe">
                        <h2 className="text-base font-bold text-sky-950">Информация для руководителя</h2>
                        <p className="mt-2 text-sm leading-6 text-sky-900">{safe.hr_notes}</p>
                    </div>
                </aside>
            </div>
        </MainLayout>
    );
}
